import React, { useState } from "react";
import * as XLSX from "xlsx-js-style";
import { useUserByRole } from "../../hooks/useUser";
import { useCurrentUser } from "../../hooks/useAuth";
import cityService from "../../services/cityService";
import countryService from "../../services/countryService";
import divisionsService from "../../services/divisionService";
import teamService from "../../services/teamService";
import roleService from "../../services/roleService";

function ExportUsersFile({ filters }) {
  const user = useCurrentUser();
  const { data: usersData } = useUserByRole({ user });
  const [showSpinning, setShowSpinning] = useState(false);

  const filterData = () => {
    if (!usersData) return [];

    return usersData.filter((user) => {
      // Role filter
      if (filters.selectedRole && user.role_id !== filters.selectedRole) {
        return false;
      }

      // Division filter
      if (
        filters.selectedDivision &&
        user.division_id !== filters.selectedDivision
      ) {
        return false;
      }

      // Status filter
      if (
        filters.selectedStatus &&
        String(user.status ?? "") !== filters.selectedStatus
      ) {
        return false;
      }

      // Search filter
      if (filters.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        const searchFields = [
          user.first_name,
          user.family_name,
          user.email_address,
          user.UPI_num,
        ];
        if (
          !searchFields.some((field) =>
            String(field || "")
              .toLowerCase()
              .includes(searchLower)
          )
        ) {
          return false;
        }
      }
      return true;
    });
  };

  const handleExport = async () => {
    setShowSpinning(true);
    try {
      const workbook = XLSX.utils.book_new();

      const headers = [
        "First Name",
        "Family Name",
        "Email",
        "UPI Number",
        "Phone Number",
        "Country",
        "City",
        "Division",
        "Team",
        "Role",
        "Medical License",
        "Specialty",
        "Organization",
      ];

      const worksheet = XLSX.utils.aoa_to_sheet([headers]);
      worksheet["!cols"] = headers.map(() => ({ width: 46 }));
      worksheet["!rows"] = [{ hpx: 28 }];

      const filteredUsers = filterData();

      if (filteredUsers.length > 0) {
        const data = await Promise.all(
          filteredUsers.map(async (user) => {
            const cityName = await cityService.getNameById(user.city_id);
            const countryName = await countryService.getNameById(
              user.country_id
            );
            const divisionName = await divisionsService.getNameById(
              user.division_id
            );
            const teamName = await teamService.getNameById(user.team_id);
            const roleName = await roleService.getNameById(user.role_id);

            return [
              user.first_name || "",
              user.family_name || "",
              user.email_address || "",
              user.UPI_num || "",
              user.phone_number || "",
              countryName || "",
              cityName || "",
              divisionName || "",
              teamName || "",
              roleName || "",
              user.medical_license || "",
              user.specialty || "",
              user.organization || "",
            ];
          })
        );
        XLSX.utils.sheet_add_aoa(worksheet, data, { origin: "A2" });
      }

      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = worksheet[cell_address];
          if (cell) {
            if (R === 0) {
              cell.s = {
                fill: { fgColor: { rgb: "CCFFCC" } },
                font: { bold: true, sz: 12 },
                alignment: { horizontal: "center", vertical: "center" },
              };
            } else {
              cell.s = {
                alignment: { horizontal: "center", vertical: "center" },
              };
            }
          }
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      XLSX.writeFile(workbook, "Users_Report.xlsx");
    } catch (error) {
      console.error("Error exporting users:", error);
    } finally {
      setShowSpinning(false);
    }
  };

  return (
    <button
      data-track="export-users-file"
      data-module="Users"
      onClick={handleExport}
      disabled={showSpinning}
      className={`px-3 py-2.5 rounded-lg flex items-center gap-x-2 text-white uppercase text-sm ${
        showSpinning ? "bg-green-600/60 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
      }`}
    >
      <i className="fa-solid fa-file-arrow-down"></i>
      {showSpinning ? "Exporting..." : "Users Report"}
      {showSpinning && (
        <svg
          className="animate-spin h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
    </button>
  );
}

export default ExportUsersFile;
